import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { X } from 'lucide-react'
import { useProjectsQuery } from '../api/projects.js'
import { TrashSections } from '../features/trash/TrashSections.js'

/**
 * Papelera (CLAUDE.md §6, fase 5): global (`/trash`, pantalla completa dentro
 * de `AppLayout`) y por proyecto (`/projects/:key/trash`, panel deslizante
 * sobre el tablero — mismo patrón que `TaskDetailPanel`, ver
 * docs/DECISIONS.md fase 4). Un único componente porque el listado es
 * idéntico; solo cambia el envoltorio visual, según si la ruta trae `:key`.
 */
export function TrashPage() {
  const { key } = useParams<{ key?: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const projects = useProjectsQuery()

  const close = () => void navigate(`/projects/${key}`)

  useEffect(() => {
    if (!key) return
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  if (!key) {
    return (
      <div className="flex flex-col gap-6 px-6 py-6">
        <h1 className="text-title font-semibold text-text">{t('trash.title')}</h1>
        <TrashSections />
      </div>
    )
  }

  const project = projects.data?.find((candidate) => candidate.key === key)

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('trash.title')}
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-[480px] flex-col border-l border-border bg-surface shadow-lg"
    >
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <h1 className="text-title font-semibold text-text">{t('trash.title')}</h1>
        <button
          type="button"
          onClick={close}
          aria-label={t('common.close')}
          className="flex h-8 w-8 max-md:h-11 max-md:w-11 items-center justify-center rounded-card text-text-muted hover:bg-surface-hi hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8">
        <TrashSections projectId={project?.id} />
      </div>
    </div>
  )
}
